'use client';

import { IconLink } from '@/uiComposites/interactive/IconLink';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { icon } from '@/uiPrimitives/media/icon';
import { arrowDownIcon } from '@/uiIcons/arrowDownIcon.css';
import { checkBulletIcon } from '@/uiIcons/checkBulletIcon.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { box } from '@/uiPrimitives/layout/box';
import { h3CSS } from '@/uiComposites/typography/headings';
import { translateInline } from '@/globals/translate/translateInline';
import { useEffect, useMemo, useRef } from 'react';
import { useContactForm } from './useContactForm';
import { addTestSelector } from '@dop/shared/helpers/testSelector';

export const IssuesSummary = () => {
	const ref = useRef<HTMLDivElement>(null);
	const [contactFormState] = useContactForm();
	const formIssueEntries = Object.entries(contactFormState?.formIssues ?? {});
	const formIssuesKey = useMemo(
		() => JSON.stringify(contactFormState?.formIssues),
		[contactFormState],
	);

	// As soon as the form issues change, trigger the scroll. We use a stringified version since useEffect only does a shallow compare
	useEffect(() => {
		if (ref.current != null) {
			ref.current.scrollIntoView({ behavior: 'smooth' });
			ref.current.focus();
		}
	}, [formIssuesKey]);

	return (
		<div role="alert">
			{contactFormState == null ||
			formIssueEntries.length === 0 ||
			contactFormState.previewMode === true ||
			contactFormState.isDirty === false ? null : (
				<div
					ref={ref}
					tabIndex={-1}
					className={classes(
						box({
							borderColor: 'positiveRed',
							borderWidth: '4px',
							borderInlineStartStyle: 'solid',
							backgroundColor: 'negativeRed',
							padding: '4 | betweenListItems',
						}),
					)}
					{...addTestSelector('uiIssuesSummary')}
				>
					<div className={classes(stack({ rowGap: '5 | betweenParagraphs' }))}>
						<h2 className={classes(h3CSS)}>
							{translateInline({
								en: 'Summary form issues',
								nl: 'Overzicht foutmeldingen',
							})}
						</h2>
						<p className={classes(textBlock({}))}>
							{formIssueEntries.length > 1
								? translateInline({
										en: 'Some form fields have invalid values. Please check the fields below and retry submitting the form.',
										nl: 'Er zijn meerdere velden niet juist ingevuld. Controleer uw gegevens en verstuur het formulier opnieuw.',
									})
								: translateInline({
										en: 'A form field has an invalid value. Please check the field below and retry submitting the form.',
										nl: 'Er is een veld niet juist ingevuld. Controleer uw gegevens en verstuur het formulier opnieuw.',
									})}
						</p>
						<ul
							className={classes(
								stack({ rowGap: '4 | betweenListItems' }),
								textStyle({ fontVariantNumeric: 'lining-nums' }),
							)}
						>
							{formIssueEntries.map(([name, issue], i) => {
								const activeIssue =
									contactFormState.fields[name]?.issue === issue;

								return (
									<li key={`${name}-${i}`}>
										<IconLink
											styling={{
												color: activeIssue
													? 'positiveBlue:noVisited'
													: 'positiveMuted',
											}}
											className={classes(
												textStyle({ color: 'positiveBlue:noVisited' }),
											)}
											icon={
												<i
													className={classes(
														icon(
															activeIssue
																? { icon: arrowDownIcon }
																: {
																		icon: checkBulletIcon,
																		fill: 'positiveGreen',
																	},
														),
													)}
												/>
											}
											href={`#form:${name}`}
											data-link-type="contactFormIssue"
											onClick={() => {
												const inputEl = document.getElementById(
													`field-${name}`,
												);
												inputEl?.focus();
											}}
										>
											{issue}
										</IconLink>
									</li>
								);
							})}
						</ul>
					</div>
				</div>
			)}
		</div>
	);
};
